import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  HttpLink,
  InMemoryCache,
  useQuery,
} from '@apollo/client';
import { createPersistedQueryLink } from '@apollo/link-persisted-queries';
import * as React from 'react';

import * as fetch from 'isomorphic-fetch';
import {
  GET_FAVORITES,
  GET_TERRITORY_CONFIGURATION,
  IFavoritesQueryVariables,
  ITerritoryConfigurationQueryVariables,
} from '../CurrentSalesPage/CurrentSalesPage.queries';

import {
  BreakpointProvider,
  Device,
  falconTheme,
  generateFalconTheme,
  IFalconTheme,
} from '@se-admin/falcon';

import { createBrowserHistory, History } from 'history';
import { Router } from 'react-router';
import { ThemeProvider } from 'styled-components';
import { TranslationsContext, UserContext } from '../../common/contexts';
import { AbTestProvider } from '../../common/contexts/AbTestContext';
import { ApplicationContext } from '../../common/contexts/ApplicationContext';
import { IQuery, ISale } from '../../common/types/apiSchema';
import { ErrorKey, ErrorType } from '../../common/utils/tracking/tracking.interfaces';
import { useErrorTracking } from '../../common/utils/tracking/trackingUtils';
import { getAuthorisationLink } from '../../utils/ProtectedResourceClient';
import { IApplicationContainerProps } from './Application.interfaces';

const history: History = createBrowserHistory();

/**
 * Sets providers for the different contexts (Application, Theme, User, Translation).
 * Also, if there is a (semi)-logged in user, it fetches favourites
 */
const ProvidersWrapper: React.FunctionComponent<IApplicationContainerProps> = props => {
  const { data: userData, error: userError, loading: userLoading } = useQuery<
    IQuery,
    IFavoritesQueryVariables
  >(GET_FAVORITES, {
    skip: !props.userId,
    variables: {
      userId: props.userId,
    },
  });

  useErrorTracking(ErrorKey.USER_DATA_LOAD, ErrorType.LOADING_ERROR, userError);

  const { data: appData, error: appError, loading: appLoading } = useQuery<
    IQuery,
    ITerritoryConfigurationQueryVariables
  >(GET_TERRITORY_CONFIGURATION, {
    variables: {
      territory: props.territory,
      userTerritory: props.userTerritory,
    },
  });

  useErrorTracking(ErrorKey.APP_DATA_LOAD, ErrorType.LOADING_ERROR, userError);

  if (appError || userError) {
    return null;
  }

  const favorites: string[] =
    appLoading || userLoading || !userData || !appData || !userData.favorites
      ? []
      : userData.favorites;
  const theme: IFalconTheme = props.theme ? generateFalconTheme(props.theme) : falconTheme;

  const defaultViewport: Device = props.defaultViewport ? Device[props.defaultViewport] : undefined;

  return (
    <BreakpointProvider defaultValue={defaultViewport}>
      <ApplicationContext.Provider
        value={{
          affiliateCode: props.affiliate,
          affiliateConfig: {
            baseUrl: '',
            isOpenSite: props.isOpenSite,
            isWhiteLabel: props.isWhiteLabel,
            urlString: '',
          },
          currency: props.currency,
          featureFlags: props.featureFlags,
          isRebookingEnabled: props.isRebookingEnabled,
          territoryCode: props.territory,
          territoryConfig: appData
            ? appData.territoryConfiguration
            : { isFlightFunctionalityEnabled: true },
          userTerritoryCode: props.userTerritory,
        }}
      >
        <ThemeProvider theme={theme}>
          <UserContext.Provider value={{ userId: props.userId, favourites: favorites }}>
            <TranslationsContext.Provider value={{ translations: props.translationObject }}>
              <AbTestProvider>{props.children}</AbTestProvider>
            </TranslationsContext.Provider>
          </UserContext.Provider>
        </ThemeProvider>
      </ApplicationContext.Provider>
    </BreakpointProvider>
  );
};

ProvidersWrapper.displayName = 'ProvidersWrapper';

/**
 * Application Container. Sets up ApolloProvider, and includes Wrappers component.
 */
const ApplicationContainer: React.FunctionComponent<IApplicationContainerProps> = props => {
  let client: ApolloClient<{}> = new ApolloClient({
    cache: new InMemoryCache({
      typePolicies: {
        SearchResults: {
          fields: {
            sales: {
              keyArgs: [],
              merge(existing, incoming, { args: { offset = 0 } }) {
                const merged: [ISale] = existing ? existing.slice(0) : [];
                for (let i: number = 0; i < incoming.length; ++i) {
                  merged[offset + i] = incoming[i];
                }

                return merged;
              },
            },
          },
        },
      },
    }),
    connectToDevTools: true,
    link: ApolloLink.from([
      ApolloLink.split(
        operation => operation.getContext().protectedFlashsales,
        getAuthorisationLink(props.refreshTokenUrl)
      ),
      ApolloLink.split(
        operation => operation.getContext().travelBird,
        (operation, forward) => forward(operation),
        createPersistedQueryLink({ useGETForHashedQueries: true })
      ),
      ApolloLink.split(
        operation => operation.getContext().travelBird,
        new HttpLink({
          fetch,
          uri: props.apiUrlTravelBird,
        }),

        new HttpLink({
          fetch,
          uri: props.apiUrl,
        })
      ),
    ]),
    resolvers: {},
  });

  [client] = React.useState<ApolloClient<{}>>(client);

  return props.apiUrl && client ? (
    <Router history={props.history || history}>
      <ApolloProvider client={client}>
        <ProvidersWrapper {...props} />
      </ApolloProvider>
    </Router>
  ) : (
    <Router history={props.history || history}>
      <ProvidersWrapper {...props} />
    </Router>
  );
};

ApplicationContainer.displayName = 'ApplicationContainer';

export default ApplicationContainer;
